import { useEffect, useRef, useState } from 'react';
import {
  DEFAULT_STORY_MAX_PHOTOS,
  DEFAULT_STORY_PHOTO_MAX_BYTES,
  STORY_PHOTO_CONTENT_TYPES,
  type PublicStoryMedia,
  type StoryPhotoContentType,
} from '@fois-utsavvoice/shared';
import { ApiError } from '../api/client.js';
import {
  completeStoryPhoto,
  initiateStoryPhoto,
  listPublicStoryMedia,
  removeStoryPhoto,
  uploadStoryPhoto,
} from '../api/stories.js';
import {
  isStoryMediaBusy,
  isStoryMediaProcessing,
  mergeLocalMediaItems,
  storyMediaStatusLabel,
  waitForStoryMediaToSettle,
} from '../lib/story-media-upload.js';

interface PhotoItem {
  localId: string;
  mediaId?: string;
  file?: File;
  previewUrl?: string;
  status: string;
  progress: number;
  error?: string;
}

const ACCEPT = STORY_PHOTO_CONTENT_TYPES.join(',');

function mimeForFile(file: File): StoryPhotoContentType | undefined {
  if ((STORY_PHOTO_CONTENT_TYPES as readonly string[]).includes(file.type)) {
    return file.type as StoryPhotoContentType;
  }
  const name = file.name.toLowerCase();
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) {
    return 'image/jpeg';
  }
  if (name.endsWith('.png')) {
    return 'image/png';
  }
  if (name.endsWith('.webp')) {
    return 'image/webp';
  }
  if (name.endsWith('.heic')) {
    return 'image/heic';
  }
  if (name.endsWith('.heif')) {
    return 'image/heif';
  }
  return undefined;
}

function fromServer(item: PublicStoryMedia, local?: PhotoItem): PhotoItem {
  return {
    localId: local?.localId ?? item.mediaId,
    mediaId: item.mediaId,
    file: local?.file,
    previewUrl: local?.previewUrl,
    status: item.status,
    progress: item.status === 'READY' || item.status === 'APPROVED' ? 100 : local?.progress ?? 0,
    error: local?.error,
  };
}

export function StoryPhotoPicker({
  token,
  onBusyChange,
  onCountChange,
}: {
  token: string;
  onBusyChange: (busy: boolean) => void;
  onCountChange?: (count: number) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const previewUrls = useRef(new Set<string>());
  const [items, setItems] = useState<PhotoItem[]>([]);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    return () => {
      for (const url of previewUrls.current) {
        URL.revokeObjectURL(url);
      }
      previewUrls.current.clear();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    listPublicStoryMedia(token)
      .then(async (result) => {
        if (cancelled) {
          return;
        }
        setItems((current) => {
          const byMediaId = new Map(
            current.filter((item) => item.mediaId).map((item) => [item.mediaId, item]),
          );
          const listed = result.items
            .filter((item) => item.mediaType === 'PHOTO')
            .map((item) => fromServer(item, byMediaId.get(item.mediaId)));
          return mergeLocalMediaItems(current, listed);
        });
        for (const item of result.items.filter((entry) => entry.mediaType === 'PHOTO')) {
          if (!isStoryMediaProcessing(item.status)) {
            continue;
          }
          const settled = await waitForStoryMediaToSettle(token, item.mediaId, item);
          if (cancelled) {
            return;
          }
          setItems((current) =>
            current.map((entry) =>
              entry.mediaId === settled.mediaId ? fromServer(settled, entry) : entry,
            ),
          );
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    onBusyChange(items.some((item) => isStoryMediaBusy(item.status)));
    onCountChange?.(items.length);
  }, [items, onBusyChange, onCountChange]);

  const remaining = DEFAULT_STORY_MAX_PHOTOS - items.length;

  function rememberPreview(file: File): string {
    const url = URL.createObjectURL(file);
    previewUrls.current.add(url);
    return url;
  }

  function forgetPreview(url: string | undefined): void {
    if (!url) {
      return;
    }
    URL.revokeObjectURL(url);
    previewUrls.current.delete(url);
  }

  async function uploadOne(file: File, localId: string): Promise<void> {
    const mime = mimeForFile(file);
    if (!mime) {
      setItems((current) =>
        current.map((item) =>
          item.localId === localId
            ? { ...item, status: 'REJECTED_AUTOMATED', error: 'Use a JPEG, PNG, WebP, or HEIC photo.' }
            : item,
        ),
      );
      return;
    }
    if (file.size > DEFAULT_STORY_PHOTO_MAX_BYTES) {
      setItems((current) =>
        current.map((item) =>
          item.localId === localId
            ? { ...item, status: 'REJECTED_AUTOMATED', error: 'That photo is larger than the 10 MB limit.' }
            : item,
        ),
      );
      return;
    }
    try {
      const grant = await initiateStoryPhoto(
        token,
        { mediaType: 'PHOTO', declaredMimeType: mime, sizeBytes: file.size },
        localId,
      );
      if (grant.uploadMode !== 'SINGLE') {
        throw new Error('PHOTO_UPLOAD_FAILED');
      }
      setItems((current) =>
        current.map((item) =>
          item.localId === localId ? { ...item, mediaId: grant.mediaId, status: 'UPLOADING' } : item,
        ),
      );
      await uploadStoryPhoto(grant.uploadUrl, file, grant.headers, (percent) => {
        setItems((current) =>
          current.map((item) => (item.localId === localId ? { ...item, progress: percent } : item)),
        );
      });
      const completed = await completeStoryPhoto(token, grant.mediaId);
      setItems((current) =>
        current.map((item) =>
          item.localId === localId
            ? { ...item, mediaId: completed.mediaId, status: completed.status, progress: 100, error: undefined }
            : item,
        ),
      );
      const settled = await waitForStoryMediaToSettle(token, grant.mediaId, completed);
      setItems((current) =>
        current.map((item) =>
          item.localId === localId
            ? {
                ...item,
                mediaId: settled.mediaId,
                status: settled.status,
                progress: 100,
                error:
                  settled.status === 'FAILED_RETRYABLE'
                    ? 'This photo is still being checked. Please retry in a moment.'
                    : undefined,
              }
            : item,
        ),
      );
    } catch (caught) {
      setItems((current) =>
        current.map((item) =>
          item.localId === localId
            ? {
                ...item,
                status: 'FAILED_RETRYABLE',
                error: caught instanceof ApiError ? caught.message : 'This photo could not be uploaded.',
              }
            : item,
        ),
      );
    }
  }

  function onPick(files: FileList | null): void {
    if (!files?.length) {
      return;
    }
    setError(undefined);
    const nextFiles = Array.from(files).slice(0, Math.max(remaining, 0));
    if (files.length > nextFiles.length) {
      setError('You can add up to 5 photos.');
    }
    const additions = nextFiles.map((file) => ({
      localId: crypto.randomUUID(),
      file,
      previewUrl: rememberPreview(file),
      status: 'UPLOADING',
      progress: 0,
    }));
    setItems((current) => [...current, ...additions]);
    for (const item of additions) {
      if (item.file) {
        void uploadOne(item.file, item.localId);
      }
    }
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  async function onRemove(item: PhotoItem): Promise<void> {
    if (item.mediaId) {
      try {
        await removeStoryPhoto(token, item.mediaId);
      } catch {
        setError('The photo could not be removed.');
        return;
      }
    }
    forgetPreview(item.previewUrl);
    setItems((current) => current.filter((entry) => entry.localId !== item.localId));
  }

  async function onRetry(item: PhotoItem): Promise<void> {
    if (!item.file) {
      return;
    }
    setItems((current) =>
      current.map((entry) =>
        entry.localId === item.localId
          ? { ...entry, status: 'UPLOADING', progress: 0, error: undefined }
          : entry,
      ),
    );
    await uploadOne(item.file, item.localId);
  }

  return (
    <div className="mt-6 space-y-3">
      <input
        ref={inputRef}
        id="story-photos"
        className="sr-only"
        type="file"
        accept={ACCEPT}
        multiple
        disabled={remaining <= 0}
        onChange={(event) => onPick(event.target.files)}
      />
      <button
        className="btn-secondary"
        type="button"
        disabled={remaining <= 0}
        onClick={() => inputRef.current?.click()}
      >
        {items.length ? 'Add another photo' : 'Choose photos'}
      </button>
      <p className="text-sm text-charcoal/70">Up to 5 photos, 10 MB each. JPEG, PNG, WebP, or HEIC.</p>
      {error ? (
        <p className="text-sm font-semibold text-crimson" role="alert">
          {error}
        </p>
      ) : null}
      {items.length ? (
        <ul className="space-y-2">
          {items.map((item, index) => (
            <li key={item.localId} className="rounded-lg border border-kraft/40 p-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-charcoal">Photo {index + 1}</p>
                <p className="text-xs uppercase tracking-wide text-charcoal/70">
                  {storyMediaStatusLabel(item.status)}
                </p>
              </div>
              {item.previewUrl ? (
                <img
                  className="mt-2 max-h-48 w-full rounded object-cover"
                  src={item.previewUrl}
                  alt={`Photo ${index + 1} preview`}
                />
              ) : null}
              {item.status === 'UPLOADING' || isStoryMediaProcessing(item.status) ? (
                <progress className="mt-2 w-full" max={100} value={item.status === 'UPLOADING' ? item.progress : 100} />
              ) : null}
              {item.error ? (
                <p className="mt-2 text-sm text-crimson" role="alert">
                  {item.error}
                </p>
              ) : null}
              <div className="mt-2 flex gap-2">
                {item.status === 'FAILED_RETRYABLE' && item.file ? (
                  <button className="btn-secondary" type="button" onClick={() => void onRetry(item)}>
                    Retry
                  </button>
                ) : null}
                <button className="btn-secondary" type="button" onClick={() => void onRemove(item)}>
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
